import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { userEndpoint, Base_URL } from "actions/API_endpoint";
import Swal from "sweetalert2";

export default function ManageProjects() {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const fetchProjects = async () => {
        try {
            const response = await fetch(`${Base_URL}${userEndpoint.getAllProjects}`);
            const data = await response.json();
            if (data.success) {
                setProjects(data.data);
            }
        } catch (error) {
            console.error("Error fetching projects:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProjects();
    }, []);

    const handleStatusChange = async (id, currentStatus) => {
        const newStatus = currentStatus === 'active' ? 'inactive' : 'active';

        try {
            const response = await fetch(`${Base_URL}${userEndpoint.updateProjectStatus(id)}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ status: newStatus })
            });
            const data = await response.json();
            if (data.success) {
                Swal.fire("Success", `Project marked as ${newStatus}`, "success");
                fetchProjects();
            } else {
                Swal.fire("Error", data.error || "Failed to update status", "error");
            }
        } catch (error) {
            console.error("Error updating status:", error);
            Swal.fire("Error", "Failed to update project status.", "error");
        }
    };

    const handleDelete = async (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this! This project will be deleted permanently.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#EF4444",
            cancelButtonColor: "#4B5563",
            confirmButtonText: "Yes, delete it!"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await fetch(`${Base_URL}${userEndpoint.deleteProject(id)}`, {
                        method: "DELETE"
                    });
                    const data = await response.json();
                    if (data.success) {
                        Swal.fire("Deleted!", "Project has been deleted.", "success");
                        fetchProjects();
                    } else {
                        Swal.fire("Error", data.message || data.error || "Failed to delete project", "error");
                    }
                } catch (error) {
                    console.error("Error deleting project:", error);
                    Swal.fire("Error", "Failed to delete project.", "error");
                }
            }
        });
    };

    return (
        <div className="flex flex-wrap mt-4">
            <div className="w-full mb-12 px-4">
                <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-dark-secondary text-white">
                    <div className="rounded-t mb-0 px-4 py-3 border-0">
                        <div className="flex flex-wrap items-center justify-between">
                            <div className="relative w-full px-4 max-w-full flex-grow flex-1">
                                <h3 className="font-semibold text-lg text-white">
                                    Manage MPDI/IPD Projects
                                </h3>
                            </div>
                            <div className="relative w-full px-4 max-w-full flex-grow flex-1 text-right">
                                <button
                                    className="bg-accent-primary text-white active:bg-accent-hover font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
                                    type="button"
                                    onClick={() => navigate("/admin/createProject")}
                                >
                                    <i className="fas fa-plus mr-2"></i> Create Project
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="block w-full overflow-x-auto min-h-[500px]">
                        {loading ? (
                            <div className="flex justify-center items-center h-64">
                                <div className="w-12 h-12 border-4 border-accent-primary border-t-transparent rounded-full animate-spin"></div>
                            </div>
                        ) : (
                            <table className="items-center w-full bg-transparent border-collapse">
                                <thead>
                                    <tr>
                                        <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-dark-tertiary text-gray-300 border-dark-tertiary">
                                            Project ID
                                        </th>
                                        <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-dark-tertiary text-gray-300 border-dark-tertiary">
                                            Project Name
                                        </th>
                                        <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-dark-tertiary text-gray-300 border-dark-tertiary">
                                            Scheme
                                        </th>
                                        <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-dark-tertiary text-gray-300 border-dark-tertiary">
                                            Team Leader
                                        </th>
                                        <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-dark-tertiary text-gray-300 border-dark-tertiary">
                                            Status
                                        </th>
                                        <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-dark-tertiary text-gray-300 border-dark-tertiary">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {projects.map((project) => (
                                        <tr key={project.id} className="border-b border-dark-tertiary hover:bg-dark-tertiary/50">
                                            <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-sm whitespace-nowrap p-4 text-gray-300 font-mono">
                                                {project.project_id || 'N/A'}
                                            </td>
                                            <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-sm whitespace-nowrap p-4 font-bold text-white">
                                                {project.project_name}
                                            </td>
                                            <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-sm whitespace-nowrap p-4 text-gray-300">
                                                <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${project.scheme === 'mpdi' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'}`}>
                                                    {project.scheme}
                                                </span>
                                            </td>
                                            <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-sm whitespace-nowrap p-4 text-gray-300">
                                                {project.team_leader_data ? (
                                                    <>{project.team_leader_data.first_name} {project.team_leader_data.last_name}<br />
                                                        <span className="text-xs text-gray-500">{project.team_leader_data.admission_no}</span></>
                                                ) : project.team_leader}
                                            </td>
                                            <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-sm whitespace-nowrap p-4 text-gray-300">
                                                <button
                                                    onClick={() => handleStatusChange(project.id, project.status)}
                                                    className={`px-2 py-1 rounded text-xs font-bold uppercase transition-colors ${project.status === 'active' ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                                                        }`}>
                                                    {project.status}
                                                </button>
                                            </td>
                                            <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-sm whitespace-nowrap p-4 text-left">
                                                <div className="flex gap-2">
                                                    <button
                                                        className="bg-purple-500 hover:bg-purple-600 text-white active:bg-purple-700 font-bold uppercase text-xs px-3 py-1 rounded shadow hover:shadow-md outline-none focus:outline-none ease-linear transition-all duration-150"
                                                        onClick={() => navigate(`/admin/editProject/${project.id}`)}
                                                    >
                                                        <i className="fas fa-edit mr-1"></i> Edit
                                                    </button>
                                                    <button
                                                        className="bg-blue-500 hover:bg-blue-600 text-white active:bg-blue-700 font-bold uppercase text-xs px-3 py-1 rounded shadow hover:shadow-md outline-none focus:outline-none ease-linear transition-all duration-150"
                                                        onClick={() => navigate(`/admin/projects/${project.id}`)}
                                                    >
                                                        <i className="fas fa-eye mr-1"></i> View / Evaluate
                                                    </button>
                                                    {(!project.evaluations || project.evaluations.length === 0) && (
                                                        <button
                                                            className="bg-red-500 hover:bg-red-600 text-white active:bg-red-700 font-bold uppercase text-xs px-3 py-1 rounded shadow hover:shadow-md outline-none focus:outline-none ease-linear transition-all duration-150"
                                                            onClick={() => handleDelete(project.id)}
                                                        >
                                                            <i className="fas fa-trash mr-1"></i> Delete
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {projects.length === 0 && (
                                        <tr>
                                            <td colSpan="5" className="text-center py-8 text-gray-400">
                                                No projects found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
